import { SegmentPartnerRevenueMethod, ThirdPartyFeeConfigRevenueMethod } from '@openx/types/gqlTypes';
import type { AudiencePricing } from '@openx/types/targeting/audience';
import { ValidationError } from 'yup';

export type SegmentPartnerShare = {
  partner_id?: string;
  share?: string | number | null;
};

export type FormSlice = {
  third_party_fees_config_enabled?: boolean;
  segment_partner_revenue_method?: string | null;
  segment_partner_platform_share?: Array<SegmentPartnerShare | null> | null;
  pricing_by_org_data?: AudiencePricing['pricing_by_org'] | null;
  contextual_max_pom?: number | null;
  third_party_fees_config?: Array<{
    revenue_method?: string | null;
    gross_share?: number | string | null;
  } | null> | null;
};

// The 94% error is displayed on the curated deal fee field, so the check runs only when that fee is a PoM
export const shouldValidate94Percent = (form: FormSlice): boolean => {
  const isFeeShareEnabled = form.third_party_fees_config_enabled === true;
  const thirdPartyRevenueMethod = form.third_party_fees_config?.[0]?.revenue_method;
  const isThirdPartyFeePoM = thirdPartyRevenueMethod === ThirdPartyFeeConfigRevenueMethod.PoM;

  return isFeeShareEnabled && isThirdPartyFeePoM;
};

export const getAudienceFeePercentValue = (form: FormSlice): number => {
  if (form.segment_partner_revenue_method !== SegmentPartnerRevenueMethod.PoM) {
    return 0;
  }

  return getMaxOrgPomValue(form.pricing_by_org_data) * 100;
};

// Contextual fees share the deal's data partner fee method. Without audience fees the method may be unset,
// and contextual-only deals default to PoM, so only an explicit CPM drops the contextual fee from the check.
export const getContextualFeePercentValue = (form: FormSlice): number => {
  if (form.segment_partner_revenue_method === SegmentPartnerRevenueMethod.Cpm) {
    return 0;
  }

  const value = Number(form.contextual_max_pom ?? 0);

  return Number.isFinite(value) ? value * 100 : 0;
};

export const getThirdPartyFeePercentValue = (form: FormSlice): number => {
  const grossShare = form.third_party_fees_config?.[0]?.gross_share;
  const value = grossShare != null && grossShare !== '' ? Number(grossShare) : 0;

  return Number.isFinite(value) ? value : 0;
};

export const getMaxOrgPomValue = (pricingByOrg: AudiencePricing['pricing_by_org'] | null | undefined): number => {
  if (!pricingByOrg || typeof pricingByOrg !== 'object') {
    return 0;
  }

  const pomValues = Object.values(pricingByOrg).map((p: AudiencePricing['pricing_by_org'][string]) => p.price_pom);

  return pomValues.length > 0 ? Math.max(...pomValues) : 0;
};

export const createPlatformFee94Error = (message: string): ValidationError => {
  return new ValidationError(message, undefined, 'third_party_fees_config.0.gross_share', 'platformFee94');
};
